import React, { useState, useEffect, useMemo, useRef, useCallback, Suspense, lazy } from 'react';
import { ShoppingCart, Plus, Minus, X, Search, Phone, Heart, ShoppingBag, User, Mail, MapPin, Package, Menu, MessageCircle, Send, Globe } from 'lucide-react';
import { API_URL, jaggeryPowderProducts, jaggeryCubesProducts, allProducts, defaultSelectedSizes } from './data/products';
import { useSEO } from './utils/seo';
import ContactPopup from './Components/ContactPopup';
import Chatbot from './Components/Chatbot';

// Lazy-loaded page components — each page becomes its own JS chunk,
// loaded only when the user navigates to that route.
// This reduces the initial bundle from ~8 MB to ~200–400 KB.
const HomePage          = lazy(() => import('./pages/HomePage'));
const ProductDetailPage = lazy(() => import('./pages/Productdetailpage'));
const ContactPage       = lazy(() => import('./pages/ContactPage'));
const AboutPage         = lazy(() => import('./pages/Aboutpage'));
const DistributorPage   = lazy(() => import('./pages/DistributorPage'));
const PolicyPage        = lazy(() => import('./pages/Policypage'));

export default function App() {
  const [user, setUser] = useState(null);
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [authError, setAuthError] = useState('');
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSizes, setSelectedSizes] = useState({ 
    // Jaggery Powder Products
    'powder-5kg': '5kg', 
    'powder-3kg': '3kg', 
    'powder-2kg': '2kg',
    'powder-1kg': '1kg',
    'powder-pet-jar-1.5kg': '1.5kg',
    'powder-combo-pouch-1.5kg': '1.5kg',
    'powder-jar-combo-1kg': '1kg',
    'powder-pouch-combo-1kg': '1kg',
    // Jaggery Cubes Products
    'cubes-5kg': '5kg',
    'cubes-3kg': '3kg',
    'cubes-2kg': '2kg',
    'cubes-1kg': '1kg',
    'cubes-pet-jar-1.5kg': '1.5kg',
    'cubes-combo-pouch-1.5kg': '1.5kg',
    'cubes-jar-combo-1kg': '1kg',
    'cubes-pouch-combo-1kg': '1kg'
  });
  const [showInStock, setShowInStock] = useState(true);
  const [showOutOfStock, setShowOutOfStock] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 10000 });
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('powder'); // 'powder' or 'cubes'
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    fullName: '', email: '', phone: '', address: '', city: '', state: '', pincode: '', paymentMethod: 'cod'
  });
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  // Add this state inside your App component

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const getPageFromURL = () => {
    const path = window.location.pathname.replace(/^\//, '') || 'home';
    return path;
  };

  const [currentPage, setCurrentPage] = useState(getPageFromURL);
  const [selectedProductId, setSelectedProductId] = useState(null);

  const navigate = (page, productId) => {
    const url = page === 'home' ? '/' : `/${page}`;
    window.history.pushState({ page }, '', url);
    setCurrentPage(page);
    setIsMobileMenuOpen(false);
    if (productId) setSelectedProductId(productId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handlePopState = (e) => {
      const page = e.state?.page || getPageFromURL();
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('popstate', handlePopState);
    window.history.replaceState({ page: getPageFromURL() }, '', window.location.pathname);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // SEO: Update title, meta, canonical, structured data on every page change
  useSEO(currentPage);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Select product categories based on selected category
  const productCategories = useMemo(() => {
    return selectedCategory === 'powder' ? jaggeryPowderProducts : jaggeryCubesProducts;
  }, [selectedCategory]);

  // Initialize selected sizes for all products
  useEffect(() => {
    const initializeSizes = {};
    [...jaggeryPowderProducts, ...jaggeryCubesProducts].forEach(product => {
      if (!selectedSizes[product.id] && product.variants.length > 0) {
        initializeSizes[product.id] = product.variants[0].weight;
      }
    });
    
    if (Object.keys(initializeSizes).length > 0) {
      setSelectedSizes(prev => ({ ...prev, ...initializeSizes }));
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) checkSession(token);
  }, []);

  const checkSession = async (token) => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        loadCart(token);
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Session check failed:', error);
    }
  };

  const loadCart = async (token) => {
    try {
      const response = await fetch(`${API_URL}/cart`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCart(data.items || []);
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
    }
  };

  const syncCart = async (newCart) => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ items: newCart })
      });
    } catch (error) {
      console.error('Failed to sync cart:', error);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    if (authMode === 'signup' && authForm.password !== authForm.confirmPassword) {
      setAuthError('Passwords do not match');
      return;
    }

    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/signup';
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: authForm.name, email: authForm.email, password: authForm.password })
      });

      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        setIsAuthModalOpen(false);
        setAuthForm({ name: '', email: '', password: '', confirmPassword: '' });
        showNotification(`Welcome ${data.user.name}!`);
      } else {
        setAuthError(data.message || 'Authentication failed');
      }
    } catch (error) {
      setAuthError('Server error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setCart([]);
    showNotification('Logged out');
  };

  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  const addToCart = (category) => {
    const size = selectedSizes[category.id] || category.variants[0]?.weight;
    const variant = category.variants.find(v => v.weight === size) || category.variants[0];
    
    if (!variant) {
      showNotification('Product variant not available');
      return;
    }
    
    const product = {
      id: `${category.id}-${size}`,
      name: category.name,
      weight: size,
      priceINR: variant.priceINR,
      originalPrice: variant.originalPrice,
      image: category.image,
      quantity: 1
    };

    const existing = cart.find(item => item.id === product.id);
    let newCart;
    if (existing) {
      newCart = cart.map(item => item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
    } else {
      newCart = [...cart, product];
    }
    setCart(newCart);
    syncCart(newCart);
    showNotification(`${product.name} added!`);
  };

  const updateQuantity = (id, change) => {
    const newCart = cart.map(item => {
      if (item.id === id) {
        const newQty = item.quantity + change;
        return newQty <= 0 ? null : { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean);
    setCart(newCart);
    syncCart(newCart);
  };

  const removeFromCart = (id) => {
    const newCart = cart.filter(item => item.id !== id);
    setCart(newCart);
    syncCart(newCart);
    showNotification('Removed');
  };

  const toggleWishlist = (id) => {
    if (wishlist.includes(id)) {
      setWishlist(wishlist.filter(i => i !== id));
    } else {
      setWishlist([...wishlist, id]);
    }
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + (item.priceINR * item.quantity), 0);
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Filter products based on availability and price
  const getFilteredProducts = () => {
    return productCategories.filter(category => {
      // Get the selected variant to check stock and price
      const selectedVariant = category.variants.find(v => v.weight === selectedSizes[category.id]) || category.variants[0];
      
      if (!selectedVariant) return false;
      
      // Check stock filter
      const stockMatch = (showInStock && selectedVariant.stock > 0) || (showOutOfStock && selectedVariant.stock === 0);
      
      // Check price filter
      const priceMatch = selectedVariant.priceINR >= priceRange.min && selectedVariant.priceINR <= priceRange.max;
      
      return stockMatch && priceMatch;
    });
  };

  const filteredProducts = getFilteredProducts();
  const PRODUCTS_PER_PAGE = 6;
  const [productPage, setProductPage] = useState(0);
  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const pagedProducts = filteredProducts.slice(productPage * PRODUCTS_PER_PAGE, (productPage + 1) * PRODUCTS_PER_PAGE);

  // ── Hero slider state (lifted out of IIFE to obey Rules of Hooks) ──
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroPrev, setHeroPrev] = useState(null);
  const [heroDir, setHeroDir] = useState(1);
  const [heroAnimating, setHeroAnimating] = useState(false);
  const [heroProgress, setHeroProgress] = useState(0);
  const heroProg = useRef(null);
  const heroIdxRef = useRef(0);
  const heroAnimRef = useRef(false);
  heroIdxRef.current = heroIdx;
  const HERO_DURATION = 5000;
  const HERO_SLIDES_LEN = 3;

  const heroGoTo = useCallback((next) => {
    if (heroAnimRef.current) return;
    const cur = heroIdxRef.current;
    const d = next > cur ? 1 : -1;
    setHeroDir(d);
    setHeroPrev(cur);
    setHeroAnimating(true);
    heroAnimRef.current = true;
    setHeroIdx(((next % HERO_SLIDES_LEN) + HERO_SLIDES_LEN) % HERO_SLIDES_LEN);
    setHeroProgress(0);
    setTimeout(() => { setHeroPrev(null); setHeroAnimating(false); heroAnimRef.current = false; }, 600);
  }, []);

  useEffect(() => {
    setHeroProgress(0);
    const start = Date.now();
    heroProg.current = setInterval(() => {
      const p = Math.min(100, ((Date.now() - start) / HERO_DURATION) * 100);
      setHeroProgress(p);
      if (p >= 100) { clearInterval(heroProg.current); heroGoTo(heroIdxRef.current + 1); }
    }, 50);
    return () => clearInterval(heroProg.current);
  }, [heroIdx]);

  // ── Carousel state (lifted out of IIFE to obey Rules of Hooks) ──
  const [carouselHovered, setCarouselHovered] = useState(null);
  const carouselScrollRef = useRef(null);
  const carouselPausedRef = useRef(false);

  useEffect(() => {
    const el = carouselScrollRef.current;
    if (!el) return;
    let animId;
    const CARD = window.innerWidth < 768 ? 160 : 210;
    const GAP = 12;
    const ITEMS_COUNT = 9;
    const speed = 0.6;
    const singleWidth = ITEMS_COUNT * (CARD + GAP);
    const step = () => {
      if (!carouselPausedRef.current) {
        el.scrollLeft += speed;
        if (el.scrollLeft >= singleWidth) el.scrollLeft -= singleWidth;
      }
      animId = requestAnimationFrame(step);
    };
    animId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animId);
  }, []);

  // Get max price from all products
  const getMaxPrice = () => {
    let max = 0;
    productCategories.forEach(category => {
      category.variants.forEach(variant => {
        if (variant.priceINR > max) max = variant.priceINR;
      });
    });
    return max;
  };

  const maxPrice = getMaxPrice();

  const clearAllFilters = () => {
    setShowInStock(true);
    setShowOutOfStock(false);
    setPriceRange({ min: 0, max: maxPrice });
    setProductPage(0);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (showInStock) count++;
    if (showOutOfStock) count++;
    return count;
  };

  const handleCheckout = () => {
    if (!user) {
      showNotification('Please login');
      setIsAuthModalOpen(true);
      return;
    }
    if (cart.length === 0) {
      showNotification('Cart is empty');
      return;
    }
    setIsCartOpen(false);
    setIsCheckoutOpen(true);
    setCheckoutForm(prev => ({ ...prev, email: user.email, fullName: user.name }));
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          items: cart,
          shippingAddress: {
            fullName: checkoutForm.fullName,
            phone: checkoutForm.phone,
            address: checkoutForm.address,
            city: checkoutForm.city,
            state: checkoutForm.state,
            pincode: checkoutForm.pincode
          },
          paymentMethod: checkoutForm.paymentMethod,
          totalAmount: getTotal()
        })
      });

      if (response.ok) {
        showNotification('Order placed!');
        setIsCheckoutOpen(false);
        setCart([]);
        syncCart([]);
      }
    } catch (error) {
      showNotification('Order failed');
    }
  };



  // ─── Render ───
  const sharedProps = {
    navigate, isMobile, selectedCategory, setSelectedCategory,
    addToCart, toggleWishlist, wishlist, searchQuery, setSearchQuery,
    selectedSizes, setSelectedSizes, showInStock, setShowInStock,
    showOutOfStock, setShowOutOfStock, priceRange, setPriceRange,
    showFilters, setShowFilters, carouselScrollRef, carouselPausedRef,
    heroIdx, setHeroIdx, heroPrev, setHeroPrev, heroDir, setHeroDir,
    heroAnimating, setHeroAnimating, heroProgress, setHeroProgress,
    heroProg, heroIdxRef, heroAnimRef, heroGoTo,
    productCategories, filteredProducts, pagedProducts, productPage,
    setProductPage, totalPages, maxPrice, clearAllFilters,
    setIsCheckoutOpen, setIsCartOpen, carouselHovered, setCarouselHovered,
    getActiveFilterCount,
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed', top: '100px', right: '30px', background: '#6B4423',
          color: 'white', padding: '16px 24px', borderRadius: '12px', zIndex: 10000,
          boxShadow: '0 8px 30px rgba(139,69,19,0.3)'
        }}>
          {notification}
        </div>
      )}

      {/* Top Bar & Header - Sticky Together */}
      <div style={{ position: 'sticky', top: 0, zIndex: 1000, background: 'white' }}>
        {/* Top Bar */}
        <div style={{ background: '#6B4423', color: 'white', padding: '10px 0', fontSize: '15px', width: '100%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 40px', display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Phone size={16} style={{ color: '#D4C4A8' }} />
              <span>+91 7373755589</span>
            </div>
            <a href="https://wa.me/917373755589" target="_blank" rel="noopener noreferrer" style={{ color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '500' }}>
              <span style={{ fontSize: '16px' }}>💬</span> Chat on WhatsApp
            </a>
          </div>
        </div>

        {/* Header */}
        <header style={{ background: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', width: '100%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '10px 40px', display: 'flex', alignItems: 'center', gap: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <a href="/" onClick={(e) => { e.preventDefault(); navigate('home'); }} style={{ display: 'flex' }}>
                <img src="https://res.cloudinary.com/dzhwcv2tr/image/upload/logo_jhaizl.png" width="160px" height="56px" alt="GUDY Logo" style={{ objectFit: 'contain' }} />
              </a>
            </div>

          <div className="desktop-search" style={{ flex: 1, maxWidth: '560px', position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={18} style={{ position: 'absolute', left: '14px', color: '#999' }} />
            <input
              type="text"
              placeholder="Search our store"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '10px 48px 10px 44px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', outline: 'none' }}
            />
            <button style={{ position: 'absolute', right: '8px', background: '#6B4423', color: 'white', border: 'none', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer' }}>
              <Search size={16} />
            </button>
          </div>

          <div className="desktop-actions" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {user ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <User size={24} style={{ color: '#6B4423' }} />
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '600' }}>{user.name}</div>
                  <button onClick={handleLogout} style={{ background: 'transparent', border: 'none', color: '#6B4423', fontSize: '14px', cursor: 'pointer', padding: 0 }}>
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setIsAuthModalOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <User size={28} />
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#6B4423' }}>ACCOUNT</div>
                  <div style={{ fontSize: '15px', color: '#666' }}>Register | Login</div>
                </div>
              </button>
            )}

            <button style={{ position: 'relative', background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px' }}>
              <Heart size={28} />
              {wishlist.length > 0 && (
                <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#D4C4A8', color: '#6B4423', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {wishlist.length}
                </span>
              )}
            </button>

            <button onClick={() => setIsCartOpen(true)} style={{ position: 'relative', background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px' }}>
              <ShoppingBag size={28} />
              {cartCount > 0 && (
                <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#D4C4A8', color: '#6B4423', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {cartCount}
                </span>
              )}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{ display: 'none', background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px' }}
          >
            <Menu size={28} style={{ color: '#6B4423' }} />
          </button>
        </div>

        {/* Mobile Search Bar */}
        <div className="mobile-search" style={{ display: 'none', padding: '0 20px 16px' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={20} style={{ position: 'absolute', left: '12px', color: '#999' }} />
            <input
              type="text"
              placeholder="Search our store"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '12px 12px 12px 40px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '15px', outline: 'none' }}
            />
          </div>
        </div>

        {/* Nav - Full Width Background */}
        <nav className="desktop-nav" style={{ background: '#f8f8f8', borderTop: '1px solid #e0e0e0', width: '100%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '10px 40px', display: 'flex', gap: '28px', alignItems: 'center' }}>
          <a href="/" onClick={(e) => { e.preventDefault(); navigate('home'); }} style={{ color: currentPage === 'home' ? '#6B4423' : '#333', textDecoration: 'none', fontSize: '15px', fontWeight: currentPage === 'home' ? '700' : '500', borderBottom: currentPage === 'home' ? '2px solid #6B4423' : '2px solid transparent', paddingBottom: '2px' }}>Home</a>
          
          {/* GUDY Products Dropdown */}
          <div 
            style={{ position: 'relative' }}
            onMouseEnter={() => setShowProductsDropdown(true)}
            onMouseLeave={() => setShowProductsDropdown(false)}
          >
            <button style={{ background: 'transparent', border: 'none', color: '#333', fontSize: '15px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 0' }}>
              GUDY Products
              <svg width="14" height="10" viewBox="0 0 12 8" fill="none" style={{ transition: 'transform 0.2s', transform: showProductsDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            {showProductsDropdown && (
              <div style={{ 
                position: 'absolute', 
                top: '100%', 
                left: 0, 
                background: 'white', 
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                borderRadius: '8px', 
                minWidth: '240px',
                zIndex: 1000,
                marginTop: '8px',
                border: '1px solid #e0e0e0',
                overflow: 'hidden'
              }}>
                <button
                  onClick={() => setSelectedCategory('powder')}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '14px 24px', color: selectedCategory === 'powder' ? '#6B4423' : '#333', background: selectedCategory === 'powder' ? '#f8f8f8' : 'white', border: 'none', borderBottom: '1px solid #f0f0f0', fontSize: '16px', cursor: 'pointer', fontWeight: selectedCategory === 'powder' ? '600' : '400' }}
                  onMouseEnter={(e) => {if (selectedCategory !== 'powder') e.target.style.background = '#f8f8f8'}}
                  onMouseLeave={(e) => {if (selectedCategory !== 'powder') e.target.style.background = 'white'}}
                >
                  Jaggery Powder
                </button>
                <button
                  onClick={() => setSelectedCategory('cubes')}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '14px 24px', color: selectedCategory === 'cubes' ? '#6B4423' : '#333', background: selectedCategory === 'cubes' ? '#f8f8f8' : 'white', border: 'none', fontSize: '16px', cursor: 'pointer', fontWeight: selectedCategory === 'cubes' ? '600' : '400' }}
                  onMouseEnter={(e) => {if (selectedCategory !== 'cubes') e.target.style.background = '#f8f8f8'}}
                  onMouseLeave={(e) => {if (selectedCategory !== 'cubes') e.target.style.background = 'white'}}
                >
                  Jaggery Cubes
                </button>
              </div>
            )}
          </div>

          <a href="/about" onClick={(e) => { e.preventDefault(); navigate('about'); }} style={{ color: currentPage === 'about' ? '#6B4423' : '#333', textDecoration: 'none', fontSize: '15px', fontWeight: currentPage === 'about' ? '700' : '500', borderBottom: currentPage === 'about' ? '2px solid #6B4423' : '2px solid transparent', paddingBottom: '2px' }}>About</a>
          <a href="/contact" onClick={(e) => { e.preventDefault(); navigate('contact'); }} style={{ color: currentPage === 'contact' ? '#6B4423' : '#333', textDecoration: 'none', fontSize: '15px', fontWeight: currentPage === 'contact' ? '700' : '500', borderBottom: currentPage === 'contact' ? '2px solid #6B4423' : '2px solid transparent', paddingBottom: '2px' }}>Contact</a>
          <a href="/distributor" onClick={(e) => { e.preventDefault(); navigate('distributor'); }} style={{ color: currentPage === 'distributor' ? '#6B4423' : '#333', textDecoration: 'none', fontSize: '15px', fontWeight: currentPage === 'distributor' ? '700' : '500', borderBottom: currentPage === 'distributor' ? '2px solid #6B4423' : '2px solid transparent', paddingBottom: '2px' }}>Distributor Enquiry</a>
          <a href="https://wa.me/917373755589" target="_blank" rel="noopener noreferrer" style={{ background: '#25D366', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 18px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginLeft: 'auto', textDecoration: 'none' }}>
            <Phone size={20} />
            Whatsapp Order
          </a>
          </div>
        </nav>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="mobile-nav" style={{
            background: 'white',
            borderTop: '1px solid #e0e0e0',
            padding: '20px',
            display: 'none'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <a href="/" onClick={(e) => { e.preventDefault(); navigate('home'); }} style={{ color: currentPage === 'home' ? '#6B4423' : '#333', textDecoration: 'none', fontSize: '17px', fontWeight: currentPage === 'home' ? '700' : '500', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>Home</a>
              
              <div>
                <div style={{ color: '#333', fontSize: '17px', fontWeight: '500', padding: '12px 0' }}>GUDY Products</div>
                <div style={{ paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <button
                    onClick={() => {
                      setSelectedCategory('powder');
                      setIsMobileMenuOpen(false);
                    }}
                    style={{ 
                      textAlign: 'left', 
                      padding: '12px', 
                      background: selectedCategory === 'powder' ? '#f8f8f8' : 'white',
                      color: selectedCategory === 'powder' ? '#6B4423' : '#666',
                      border: '1px solid #e0e0e0', 
                      borderRadius: '8px',
                      fontSize: '16px',
                      cursor: 'pointer',
                      fontWeight: selectedCategory === 'powder' ? '600' : '400'
                    }}
                  >
                    Jaggery Powder
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCategory('cubes');
                      setIsMobileMenuOpen(false);
                    }}
                    style={{ 
                      textAlign: 'left', 
                      padding: '12px', 
                      background: selectedCategory === 'cubes' ? '#f8f8f8' : 'white',
                      color: selectedCategory === 'cubes' ? '#6B4423' : '#666',
                      border: '1px solid #e0e0e0', 
                      borderRadius: '8px',
                      fontSize: '16px',
                      cursor: 'pointer',
                      fontWeight: selectedCategory === 'cubes' ? '600' : '400'
                    }}
                  >
                    Jaggery Cubes
                  </button>
                </div>
              </div>

              <a href="/about" onClick={(e) => { e.preventDefault(); navigate('about'); }} style={{ color: currentPage === 'about' ? '#6B4423' : '#333', textDecoration: 'none', fontSize: '17px', fontWeight: currentPage === 'about' ? '700' : '500', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>About</a>
              <a href="/contact" onClick={(e) => { e.preventDefault(); navigate('contact'); }} style={{ color: currentPage === 'contact' ? '#6B4423' : '#333', textDecoration: 'none', fontSize: '17px', fontWeight: currentPage === 'contact' ? '700' : '500', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>Contact</a>
              <a href="/distributor" onClick={(e) => { e.preventDefault(); navigate('distributor'); }} style={{ color: currentPage === 'distributor' ? '#6B4423' : '#333', textDecoration: 'none', fontSize: '17px', fontWeight: currentPage === 'distributor' ? '700' : '500', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>Distributor Enquiry</a>

              <div style={{ display: 'flex', gap: '12px', paddingTop: '12px' }}>
                <button 
                  onClick={() => setIsCartOpen(true)}
                  style={{ 
                    flex: 1,
                    position: 'relative',
                    background: '#6B4423', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    padding: '12px', 
                    fontSize: '15px', 
                    fontWeight: '600', 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <ShoppingBag size={20} />
                  Cart ({cartCount})
                </button>

                {user ? (
                  <button
                    onClick={handleLogout}
                    style={{ 
                      flex: 1,
                      background: '#D4C4A8',
                      color: '#6B4423',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Logout
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setIsAuthModalOpen(true);
                      setIsMobileMenuOpen(false);
                    }}
                    style={{ 
                      flex: 1,
                      background: '#D4C4A8',
                      color: '#6B4423',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Login
                  </button>
                )}
              </div>

              <a href="https://wa.me/917373755589" target="_blank" rel="noopener noreferrer" style={{ 
                background: '#25D366', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                padding: '14px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: '10px', 
                fontSize: '16px', 
                fontWeight: '600', 
                cursor: 'pointer',
                marginTop: '8px',
                textDecoration: 'none'
              }}>
                <Phone size={20} />
                Whatsapp Order
              </a>
            </div>
          </div>
        )}
      </header>
      </div>
      {/* End Top Bar & Header Sticky Wrapper */}

      {/* Auth Modal */}
      {isAuthModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }} onClick={() => setIsAuthModalOpen(false)}>
          <div style={{ background: 'white', borderRadius: '24px', padding: '40px', maxWidth: '480px', width: '90%', maxHeight: '90vh', overflow: 'auto', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setIsAuthModalOpen(false)} style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px' }}>
              <X size={24} />
            </button>
            
            <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px', textAlign: 'center' }}>{authMode === 'login' ? 'Welcome Back' : 'Create Account'}</h2>
            <p style={{ fontSize: '15px', color: '#666', marginBottom: '32px', textAlign: 'center' }}>
              {authMode === 'login' ? 'Sign in to continue' : 'Join us today'}
            </p>

            <form onSubmit={handleAuth} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {authMode === 'signup' && (
                <div>
                  <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>Name</label>
                  <input
                    type="text"
                    value={authForm.name}
                    onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                    style={{ width: '100%', padding: '14px 16px', border: '2px solid #e0e0e0', borderRadius: '12px', fontSize: '15px', outline: 'none' }}
                    required
                  />
                </div>
              )}

              <div>
                <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>Email</label>
                <input
                  type="email"
                  value={authForm.email}
                  onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                  style={{ width: '100%', padding: '14px 16px', border: '2px solid #e0e0e0', borderRadius: '12px', fontSize: '15px', outline: 'none' }}
                  required
                />
              </div>

              <div>
                <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>Password</label>
                <input
                  type="password"
                  value={authForm.password}
                  onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                  style={{ width: '100%', padding: '14px 16px', border: '2px solid #e0e0e0', borderRadius: '12px', fontSize: '15px', outline: 'none' }}
                  required
                />
              </div>

              {authMode === 'signup' && (
                <div>
                  <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>Confirm Password</label>
                  <input
                    type="password"
                    value={authForm.confirmPassword}
                    onChange={(e) => setAuthForm({ ...authForm, confirmPassword: e.target.value })}
                    style={{ width: '100%', padding: '14px 16px', border: '2px solid #e0e0e0', borderRadius: '12px', fontSize: '15px', outline: 'none' }}
                    required
                  />
                </div>
              )}

              {authError && (
                <div style={{ background: '#fee', color: '#c33', padding: '12px', borderRadius: '8px', fontSize: '14px', textAlign: 'center' }}>
                  {authError}
                </div>
              )}

              <button type="submit" style={{ padding: '16px', background: 'linear-gradient(135deg, #6B4423 0%, #8B5A3C 100%)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}>
                {authMode === 'login' ? 'Sign In' : 'Create Account'}
              </button>

              <div style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                {authMode === 'login' ? (
                  <p>
                    Don't have an account?{' '}
                    <button type="button" onClick={() => { setAuthMode('signup'); setAuthError(''); }} style={{ background: 'transparent', border: 'none', color: '#6B4423', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline' }}>
                      Sign up
                    </button>
                  </p>
                ) : (
                  <p>
                    Already have an account?{' '}
                    <button type="button" onClick={() => { setAuthMode('login'); setAuthError(''); }} style={{ background: 'transparent', border: 'none', color: '#6B4423', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline' }}>
                      Sign in
                    </button>
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cart Sidebar */}
      {isCartOpen && (
        <>
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998 }} onClick={() => setIsCartOpen(false)}></div>
          <div style={{ position: 'fixed', top: 0, right: 0, width: '450px', maxWidth: '100%', height: '100%', background: 'white', zIndex: 9999, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 20px rgba(0,0,0,0.1)' }}>
            <div style={{ padding: '30px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '24px', fontWeight: '700' }}>Cart</h2>
              <button onClick={() => setIsCartOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px' }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '20px 30px' }}>
              {cart.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '20px' }}>
                  <ShoppingCart size={64} style={{ color: '#ccc' }} />
                  <p style={{ fontSize: '18px', color: '#999' }}>Your cart is empty</p>
                </div>
              ) : (
                cart.map(item => (
                  <div key={item.id} style={{ display: 'flex', gap: '16px', padding: '20px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <img src={item.image} alt={item.name} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '12px' }} />
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>{item.name}</h3>
                      <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>{item.weight}</p>
                      <p style={{ fontSize: '18px', fontWeight: '700', color: '#6B4423' }}>₹{item.priceINR}</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-end' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#f8f8f8', borderRadius: '8px', padding: '4px' }}>
                        <button onClick={() => updateQuantity(item.id, -1)} style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '6px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                          <Minus size={16} />
                        </button>
                        <span style={{ fontSize: '16px', fontWeight: '600', minWidth: '24px', textAlign: 'center' }}>{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.id, 1)} style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '6px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                          <Plus size={16} />
                        </button>
                      </div>
                      <button onClick={() => removeFromCart(item.id)} style={{ background: '#fee', border: 'none', borderRadius: '6px', padding: '6px', cursor: 'pointer', color: '#c33' }}>
                        <X size={20} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div style={{ padding: '30px', borderTop: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <span style={{ fontSize: '18px', fontWeight: '600' }}>Total:</span>
                  <span style={{ fontSize: '28px', fontWeight: '700', color: '#6B4423' }}>₹{getTotal()}</span>
                </div>
                <button onClick={handleCheckout} style={{ width: '100%', padding: '18px', background: 'linear-gradient(135deg, #6B4423 0%, #8B5A3C 100%)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}>
                  Checkout
                </button>
              </div>
            )}
          </div>
        </>
      )}



      {/* Checkout Modal */}
      {isCheckoutOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }} onClick={() => setIsCheckoutOpen(false)}>
          <div style={{ background: 'white', borderRadius: '24px', maxWidth: '800px', width: '90%', maxHeight: '90vh', overflow: 'auto', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '30px', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
              <h2 style={{ fontSize: '28px', fontWeight: '700' }}>Checkout</h2>
              <button onClick={() => setIsCheckoutOpen(false)} style={{ position: 'absolute', top: '30px', right: '30px', background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px' }}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handlePlaceOrder} style={{ padding: '30px' }}>
              <div style={{ marginBottom: '32px', padding: '24px', background: '#f8f8f8', borderRadius: '16px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>Shipping Info</h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>Name</label>
                    <input type="text" value={checkoutForm.fullName} onChange={(e) => setCheckoutForm({ ...checkoutForm, fullName: e.target.value })} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e0e0e0', borderRadius: '12px', fontSize: '15px', outline: 'none' }} required />
                  </div>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>Email</label>
                    <input type="email" value={checkoutForm.email} onChange={(e) => setCheckoutForm({ ...checkoutForm, email: e.target.value })} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e0e0e0', borderRadius: '12px', fontSize: '15px', outline: 'none' }} required />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>Phone</label>
                  <input type="tel" value={checkoutForm.phone} onChange={(e) => setCheckoutForm({ ...checkoutForm, phone: e.target.value })} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e0e0e0', borderRadius: '12px', fontSize: '15px', outline: 'none' }} required />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>Address</label>
                  <textarea value={checkoutForm.address} onChange={(e) => setCheckoutForm({ ...checkoutForm, address: e.target.value })} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e0e0e0', borderRadius: '12px', fontSize: '15px', outline: 'none', fontFamily: 'inherit', resize: 'vertical' }} rows="3" required />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>City</label>
                    <input type="text" value={checkoutForm.city} onChange={(e) => setCheckoutForm({ ...checkoutForm, city: e.target.value })} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e0e0e0', borderRadius: '12px', fontSize: '15px', outline: 'none' }} required />
                  </div>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>State</label>
                    <input type="text" value={checkoutForm.state} onChange={(e) => setCheckoutForm({ ...checkoutForm, state: e.target.value })} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e0e0e0', borderRadius: '12px', fontSize: '15px', outline: 'none' }} required />
                  </div>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>Pincode</label>
                    <input type="text" value={checkoutForm.pincode} onChange={(e) => setCheckoutForm({ ...checkoutForm, pincode: e.target.value })} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e0e0e0', borderRadius: '12px', fontSize: '15px', outline: 'none' }} required />
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '32px', padding: '24px', background: '#f8f8f8', borderRadius: '16px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>Payment</h3>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: 'white', borderRadius: '12px', marginBottom: '12px', cursor: 'pointer' }}>
                  <input type="radio" name="payment" value="cod" checked={checkoutForm.paymentMethod === 'cod'} onChange={(e) => setCheckoutForm({ ...checkoutForm, paymentMethod: e.target.value })} style={{ width: '20px', height: '20px' }} />
                  <span>Cash on Delivery</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: 'white', borderRadius: '12px', cursor: 'pointer' }}>
                  <input type="radio" name="payment" value="online" checked={checkoutForm.paymentMethod === 'online'} onChange={(e) => setCheckoutForm({ ...checkoutForm, paymentMethod: e.target.value })} style={{ width: '20px', height: '20px' }} />
                  <span>Online Payment</span>
                </label>
              </div>

              <div style={{ marginBottom: '32px', padding: '24px', background: '#f8f8f8', borderRadius: '16px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>Order Summary</h3>
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px' }}>
                  {cart.map(item => (
                    <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0', fontSize: '15px' }}>
                      <span>{item.name} ({item.weight}) × {item.quantity}</span>
                      <span>₹{item.priceINR * item.quantity}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '16px', marginTop: '12px', borderTop: '2px solid #e0e0e0' }}>
                    <span style={{ fontSize: '18px', fontWeight: '600' }}>Total</span>
                    <span style={{ fontSize: '24px', fontWeight: '700', color: '#6B4423' }}>₹{getTotal()}</span>
                  </div>
                </div>
              </div>

              <button type="submit" style={{ width: '100%', padding: '18px', background: 'linear-gradient(135deg, #6B4423 0%, #8B5A3C 100%)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '18px', fontWeight: '600', cursor: 'pointer' }}>
                Place Order
              </button>
            </form>
          </div>
        </div>
      )}


      {/* ===== PAGE ROUTER ===== */}
      <Suspense fallback={
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '60vh', flexDirection: 'column', gap: '16px'
        }}>
          <div style={{
            width: '48px', height: '48px', border: '4px solid #F0E0CC',
            borderTop: '4px solid #6B4423', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      }>
        {currentPage === 'home' && <HomePage {...sharedProps} />}
        {currentPage === 'product' && (
          <ProductDetailPage
            navigate={navigate}
            selectedProductId={selectedProductId}
            isMobile={isMobile}
            addToCart={addToCart}
            toggleWishlist={toggleWishlist}
            wishlist={wishlist}
            setSelectedCategory={setSelectedCategory}
            setIsCheckoutOpen={setIsCheckoutOpen}
          />
        )}
        {currentPage === 'contact' && (
          <ContactPage
            navigate={navigate}
            isMobile={isMobile}
            contactForm={contactForm}
            setContactForm={setContactForm}
            contactSubmitted={contactSubmitted}
            setContactSubmitted={setContactSubmitted}
            contactLoading={contactLoading}
            setContactLoading={setContactLoading}
            showNotification={showNotification}
          />
        )}
        {currentPage === 'about' && <AboutPage navigate={navigate} isMobile={isMobile} />}
        {currentPage === 'distributor' && <DistributorPage navigate={navigate} isMobile={isMobile} />}
        {['faq','track-order','privacy-policy','terms','shipping-policy','refund-policy'].includes(currentPage) && (
          <PolicyPage currentPage={currentPage} navigate={navigate} />
        )}
      </Suspense>

      <footer style={{ background: '#F5EAD8', padding: '60px 0 0' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 40px' }}>
          {/* Top Section with Description and Contact */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '60px', marginBottom: '50px' }}>
            {/* Brand Description */}
            <div>
              <img src="https://res.cloudinary.com/dzhwcv2tr/image/upload/logo_jhaizl.png" width="200px" height="80px" alt="GUDY Logo" style={{ objectFit: 'contain', marginBottom: '20px' }} />
              <p style={{ fontSize: '15px', color: '#5A4A3A', lineHeight: '1.8' }}>
                Our commitment to excellence and customer satisfaction is unwavering, and we take pride in bringing you premium jaggery and sugar products that are not only healthy but also add natural sweetness to your meals. Try GUDY today and experience the difference!
              </p>
            </div>

            {/* Address */}
            <div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#6B4423', marginBottom: '20px' }}>Address</h3>
              <div style={{ display: 'flex', gap: '12px' }}>
                <MapPin size={24} style={{ color: '#6B4423', flexShrink: 0, marginTop: '2px' }} />
                <p style={{ fontSize: '14px', color: '#5A4A3A', lineHeight: '1.6' }}>
                  Near Holy Angels Matric Hr. Sec. School, Covent Road, Salem, Tamil Nadu, India - 636016
                </p>
              </div>
            </div>

            {/* Get in Touch */}
            <div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#6B4423', marginBottom: '20px' }}>Get in touch</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Phone size={20} style={{ color: '#6B4423' }} />
                  <span style={{ fontSize: '14px', color: '#5A4A3A' }}>+91 7373755589</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Mail size={20} style={{ color: '#6B4423' }} />
                  <span style={{ fontSize: '14px', color: '#5A4A3A' }}>consumerfeedback@gudy.com</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Mail size={20} style={{ color: '#6B4423' }} />
                  <span style={{ fontSize: '14px', color: '#5A4A3A' }}>office.gudy@gmail.com</span>
                </div>
              </div>

              {/* Social Media */}
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#6B4423', margin: '24px 0 14px' }}>Follow Us</h3>
              <div style={{ display: 'flex', gap: '12px' }}>
                {/* Facebook */}
                <a
                  href="https://www.facebook.com/profile.php?id=61588436229946"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="GUDY on Facebook"
                  style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#1877F2', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', flexShrink: 0 }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                  </svg>
                </a>
                {/* Instagram */}
                <a
                  href="https://www.instagram.com/gudy_organics/"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="GUDY on Instagram"
                  style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', flexShrink: 0 }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                  </svg>
                </a>
                {/* WhatsApp */}
                <a
                  href="https://wa.me/917373755589"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="GUDY on WhatsApp"
                  style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', flexShrink: 0 }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '40px', marginBottom: '50px', padding: '30px 0', borderTop: '1px solid rgba(107, 68, 35, 0.2)', borderBottom: '1px solid rgba(107, 68, 35, 0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ width: '70px', height: '70px', borderRadius: '50%', background: 'white', border: '3px solid #6B4423', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Package size={32} style={{ color: '#6B4423' }} />
              </div>
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#5A4A3A' }}>Express Delivery</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ width: '70px', height: '70px', borderRadius: '50%', background: 'white', border: '3px solid #6B4423', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ShoppingCart size={32} style={{ color: '#6B4423' }} />
              </div>
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#5A4A3A' }}>Secured payment</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ width: '70px', height: '70px', borderRadius: '50%', background: 'white', border: '3px solid #6B4423', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Heart size={32} style={{ color: '#6B4423' }} />
              </div>
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#5A4A3A' }}>Good Quality</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ width: '70px', height: '70px', borderRadius: '50%', background: 'white', border: '3px solid #6B4423', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <User size={32} style={{ color: '#6B4423' }} />
              </div>
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#5A4A3A' }}>Customer Support</span>
            </div>
          </div>

          {/* Footer Links */}
          {(() => {
            const linkStyle = { fontSize: '14px', color: '#5A4A3A', textDecoration: 'none', paddingBottom: '12px', borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: '1px dotted rgba(90, 74, 58, 0.3)', transition: 'color 0.3s ease', cursor: 'pointer', background: 'none', textAlign: 'left', fontFamily: 'inherit', display: 'block', width: '100%', padding: '0 0 12px 0' };
            const hoverOn = (e) => e.currentTarget.style.color = '#6B4423';
            const hoverOff = (e) => e.currentTarget.style.color = '#5A4A3A';
            const FooterLink = ({ label, onClick, href }) => (
              <a href={href || '#'} onClick={(e) => { if (onClick) { e.preventDefault(); onClick(); } }}
                style={linkStyle} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>{label}</a>
            );
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '40px', paddingBottom: '40px' }}>

                {/* Categories */}
                <div>
                  <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#6B4423', marginBottom: '20px' }}>Categories</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <FooterLink label="Jaggery Powder" href="/" onClick={() => { navigate('home'); setSelectedCategory('powder'); }} />
                    <FooterLink label="Jaggery Cubes" href="/" onClick={() => { navigate('home'); setSelectedCategory('cubes'); }} />
                    <FooterLink label="Bulk Orders" href="/contact" onClick={() => navigate('contact')} />
                    <FooterLink label="About GUDY" href="/about" onClick={() => navigate('about')} />
                  </div>
                </div>

                {/* Products */}
                <div>
                  <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#6B4423', marginBottom: '20px' }}>Products</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <FooterLink label="Jaggery Powder 1KG" href="/product" onClick={() => navigate('product', 'powder-1kg')} />
                    <FooterLink label="Jaggery Powder 3KG" href="/product" onClick={() => navigate('product', 'powder-3kg')} />
                    <FooterLink label="Jaggery Powder 5KG" href="/product" onClick={() => navigate('product', 'powder-5kg')} />
                    <FooterLink label="Jaggery Cubes 1KG" href="/product" onClick={() => navigate('product', 'cubes-1kg')} />
                    <FooterLink label="Jaggery Cubes 3KG" href="/product" onClick={() => navigate('product', 'cubes-3kg')} />
                    <FooterLink label="Jaggery Cubes 5KG" href="/product" onClick={() => navigate('product', 'cubes-5kg')} />
                  </div>
                </div>

                {/* Terms & Conditions */}
                <div>
                  <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#6B4423', marginBottom: '20px' }}>Terms & Conditions</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      { label: 'FAQ', page: 'faq' },
                      { label: 'Track Order', page: 'track-order' },
                      { label: 'Privacy Policy', page: 'privacy-policy' },
                      { label: 'Terms & Conditions', page: 'terms' },
                      { label: 'Shipping Policy', page: 'shipping-policy' },
                      { label: 'Cancellation & Refund Policy', page: 'refund-policy' },
                    ].map(({ label, page }) => (
                      <FooterLink key={page} label={label} href={`/${page}`} onClick={() => navigate(page)} />
                    ))}
                  </div>
                </div>

                {/* My Account */}
                <div>
                  <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#6B4423', marginBottom: '20px' }}>My Account</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <FooterLink label="My Account" onClick={() => { if (user) { showNotification(`Logged in as ${user.name}`); } else { setIsAuthModalOpen(true); setAuthMode('login'); } }} />
                    <FooterLink label="My Cart" onClick={() => setIsCartOpen(true)} />
                    <FooterLink label="Track My Order" href="/track-order" onClick={() => navigate('track-order')} />
                    <FooterLink label="My Wishlist" onClick={() => showNotification(wishlist.length > 0 ? `You have ${wishlist.length} item(s) in your wishlist` : 'Your wishlist is empty')} />
                    <FooterLink label="Contact Support" href="/contact" onClick={() => navigate('contact')} />
                    {user
                      ? <FooterLink label="Logout" onClick={() => handleLogout()} />
                      : <FooterLink label="Register / Login" onClick={() => { setIsAuthModalOpen(true); setAuthMode('login'); }} />
                    }
                  </div>
                </div>

              </div>
            );
          })()}


          {/* Copyright */}
          <div style={{ background: '#E8D5BC', padding: '20px 0', marginLeft: '-40px', marginRight: '-40px', textAlign: 'center' }}>
            <p style={{ fontSize: '14px', color: '#5A4A3A' }}>© 2025 GUDY. All rights reserved.</p>
          </div>
        </div>
      </footer>



      {/* ═══════════════ RESPONSIVE STYLES ═══════════════ */}
      <style>{`
        * { box-sizing: border-box; }

        /* ── PRODUCT CAROUSEL ── */
        @media (max-width: 768px) {
          .carousel-grid { grid-template-columns: 1fr !important; }
          .carousel-image { display: none !important; }
        }

        /* ── HEADER ── */
        @media (max-width: 768px) {
          nav { display: none !important; }
          .desktop-search { display: none !important; }
          .desktop-actions { display: none !important; }
          .desktop-nav { display: none !important; }
          .mobile-search { display: block !important; }
          .mobile-menu-btn { display: block !important; margin-left: auto !important; }
          .mobile-nav { display: block !important; }
          header > div { padding: 10px 16px !important; gap: 10px !important; flex-wrap: wrap !important; }
          header > div > div:first-child img { width: 130px !important; height: auto !important; }
          div[style*="background: #6B4423"][style*="padding: 8px"] { font-size: 11px !important; padding: 6px 0 !important; }
        }

        /* ── PRODUCTS SECTION ── */
        @media (max-width: 768px) {
          .products-header { flex-direction: column !important; gap: 10px !important; align-items: flex-start !important; margin-bottom: 20px !important; }
          .products-header h1 { font-size: 20px !important; }
          .mobile-filter-btn { display: block !important; width: 100% !important; }
          .desktop-sort { display: none !important; }
          .products-grid-container { grid-template-columns: 1fr !important; gap: 16px !important; }
          .filter-sidebar { display: none !important; }
          .filter-sidebar.mobile-visible { display: block !important; position: fixed !important; top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important; z-index: 1000 !important; border-radius: 0 !important; height: 100vh !important; overflow-y: auto !important; margin: 0 !important; }
          .filter-sidebar.mobile-visible .mobile-filter-close { display: block !important; }
          .product-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 12px !important; }
          .product-grid > div > div:first-child { height: 180px !important; }
          .product-grid h3 { font-size: 13px !important; }
          .product-grid p { font-size: 12px !important; }
        }
        @media (max-width: 380px) {
          .product-grid { grid-template-columns: 1fr !important; }
          .product-grid > div > div:first-child { height: 220px !important; }
        }

        /* ── CONTACT PAGE ── */
        @media (max-width: 768px) {
          .contact-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
          .contact-form-box { padding: 24px 20px !important; }
          .contact-info-cards { order: 2 !important; }
          .contact-form-col { order: 1 !important; }
        }

        /* ── ABOUT PAGE ── */
        @media (max-width: 768px) {
          .about-grid { grid-template-columns: 1fr !important; gap: 30px !important; }
          .about-feature-grid { grid-template-columns: repeat(2,1fr) !important; }
          .about-values-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
          .about-cta-btns { flex-direction: column !important; }
          .about-hero h1 { font-size: 30px !important; }
        }

        /* ── POLICY PAGES ── */
        @media (max-width: 768px) {
          .policy-header { padding: 40px 20px !important; }
          .policy-header h1 { font-size: 24px !important; }
          .policy-content { padding: 28px 20px 50px !important; }
          .policy-back-btns { flex-direction: column !important; }
        }

        /* ── FOOTER ── */
        @media (max-width: 768px) {
          footer { padding: 28px 0 0 !important; }
          footer > div { padding: 0 16px !important; }
          footer > div > div:first-child { grid-template-columns: 1fr !important; gap: 20px !important; }
          footer > div > div:first-child img { width: 110px !important; }
          footer > div > div:nth-child(2) { grid-template-columns: repeat(2,1fr) !important; gap: 12px !important; }
          footer > div > div:nth-child(3) { grid-template-columns: 1fr !important; gap: 20px !important; }
          footer > div > div:last-child { margin-left:-16px !important; margin-right:-16px !important; padding: 14px 0 !important; }
        }

        /* ── MODALS & CART ── */
        @media (max-width: 768px) {
          div[style*="position: fixed"][style*="rgba(0,0,0,0.6)"] > div { width: 96% !important; max-width: 96% !important; max-height: 92vh !important; border-radius: 14px !important; }
          div[style*="position: fixed"][style*="rgba(0,0,0,0.6)"] > div > div { padding: 18px !important; }
          form > div > div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; gap: 14px !important; }
        }

        /* ── NOTIFICATION ── */
        @media (max-width: 768px) {
          div[style*="position: fixed"][style*="top: 100px"] { right: 10px !important; left: 10px !important; top: 68px !important; font-size: 13px !important; padding: 11px 14px !important; }
        }

        /* ── TABLET ── */
        @media (min-width: 769px) and (max-width: 1024px) {
          .products-grid-container { grid-template-columns: 220px 1fr !important; }
          .product-grid { grid-template-columns: repeat(2,1fr) !important; }
          footer > div > div:nth-child(3) { grid-template-columns: repeat(2,1fr) !important; }
          header > div > div:first-child img { width: 180px !important; height: auto !important; }
        }
        @media (min-width: 1025px) and (max-width: 1200px) {
          .product-grid { grid-template-columns: repeat(3,1fr) !important; }
        }
      `}</style>
   <ContactPopup 
  isOpen={isContactPopupOpen} 
  onClose={() => setIsContactPopupOpen(false)} 
/>

{/* Contact Button */}
<button
  onClick={() => setIsContactPopupOpen(true)}
  style={{
    position: 'fixed',
    bottom: '90px',
    right: '20px',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #8B5A3C 0%, #6B4423 100%)',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(107,68,35,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999
  }}
>
    <Phone size={26}/>
</button>
      {/* Chatbot Button */}
      <button
        onClick={() => setIsChatbotOpen(!isChatbotOpen)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: isChatbotOpen ? (isMobile ? '20px' : '420px') : '20px',
          display: isChatbotOpen && isMobile ? 'none' : 'flex',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #6B4423 0%, #8B5A3C 100%)',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(107,68,35,0.4)',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999,
          transition: 'all 0.3s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        {isChatbotOpen ? <X size={28} /> : <MessageCircle size={28} />}
      </button>

      {/* Chatbot */}
      <Chatbot isOpen={isChatbotOpen} onClose={() => setIsChatbotOpen(false)} />

    </div>
  );

}